import { useCallback, useEffect, useRef } from "react";
import { trpc } from "../api/trpc";
import { createLogger } from "../utils/logger";
import { getAuthToken } from "../api/axios";
import type { ReadiumLocatorText } from "@chatable/schemas/reading/locator";

const logger = createLogger("useReadingProgress");

interface ProgressData {
  chapterHref?: string;
  position?: number;
  progression?: number;
  totalProgression?: number;
  cfi?: string;
  locatorText?: ReadiumLocatorText;
}

interface PendingProgress {
  bookId: string;
  versionId: string | null;
  data: ProgressData;
}

/**
 * Sync progress using fetch with keepalive option.
 * This ensures the request completes even during page navigation/unmount.
 * Uses keepalive: true which guarantees the request completes even if the page unloads.
 */
function syncProgressWithKeepalive(
  bookId: string,
  versionId: string | null,
  data: ProgressData,
): void {
  const token = getAuthToken();
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Fire-and-forget with keepalive - request will complete even during navigation
  // Note: keepalive requests have a 64KB body limit, which is fine for progress data
  globalThis
    .fetch(`${apiBaseUrl}/trpc/readingProgress.updateProgress`, {
      method: "POST",
      headers,
      body: JSON.stringify({ json: { bookId, versionId, ...data } }),
      credentials: "include",
      keepalive: true,
    })
    .then(() => {
      logger.debug("🚀 Synced progress on unmount", { bookId, ...data });
    })
    .catch(error => {
      logger.error("Failed to sync progress on unmount", { bookId, error });
    });
}

/**
 * Hook to get reading progress for a book
 *
 * @param bookId - ID of the book
 * @returns Query for reading progress
 */
export const useReadingProgress = (
  bookId: string | undefined,
  versionId: string | null | undefined,
) => {
  return trpc.readingProgress.getProgress.useQuery(
    { bookId: bookId || "", versionId },
    {
      enabled: !!bookId && versionId !== undefined,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: "always", // Always refetch when navigating back to book page
    },
  );
};

/**
 * Hook to sync reading progress with debouncing
 *
 * Debounces updates to avoid excessive API calls during rapid navigation.
 * Also syncs any pending progress on unmount.
 *
 * @param bookId - ID of the book
 * @returns Object with syncProgress function and loading state
 */
export const useSyncReadingProgress = (
  bookId: string | undefined,
  versionId: string | null | undefined,
) => {
  const updateMutation = trpc.readingProgress.updateProgress.useMutation();
  const pendingRef = useRef<PendingProgress | null>(null);
  const timeoutRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  // Store mutate in a ref to prevent syncProgress from changing on every render
  // This fixes React error #185 (infinite re-render loop) in ProgressTracker
  const mutateRef = useRef(updateMutation.mutate);

  // Keep mutateRef in sync with the latest mutate function
  useEffect(() => {
    mutateRef.current = updateMutation.mutate;
  }, [updateMutation.mutate]);

  // Keep the callback stable for a source identity, while each queued write
  // retains the book and version that produced its locator.
  const syncProgress = useCallback(
    (data: ProgressData) => {
      if (!bookId || versionId === undefined) return;

      pendingRef.current = { bookId, versionId, data };

      // Clear existing timeout
      if (timeoutRef.current) {
        globalThis.clearTimeout(timeoutRef.current);
      }

      // Debounce: wait 2 seconds before syncing
      timeoutRef.current = globalThis.setTimeout(() => {
        const pending = pendingRef.current;
        if (pending) {
          // Debug logging
          logger.debug("🚀 Syncing progress to API", {
            bookId: pending.bookId,
            ...pending.data,
          });
          mutateRef.current({
            bookId: pending.bookId,
            versionId: pending.versionId,
            ...pending.data,
          });
          pendingRef.current = null;
        }
      }, 2000);
    },
    [bookId, versionId],
  );

  // Sync pending progress on unmount using keepalive fetch
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        globalThis.clearTimeout(timeoutRef.current);
      }
      if (pendingRef.current) {
        // Use keepalive fetch to ensure request completes during navigation
        const pending = pendingRef.current;
        syncProgressWithKeepalive(pending.bookId, pending.versionId, pending.data);
      }
    };
  }, []); // Empty dependency array

  return {
    syncProgress,
    isUpdating: updateMutation.isPending,
  };
};
